import { z } from "zod";
import { MAX_STOCK_QTY_Q4 } from "@cx/domain";
import { listQuery } from "./common";
import { text } from "./fields";
import { ulidParam } from "./ids";
import { lotInput, lotTakes, type MovementLotView } from "./lots";

/** E entrada | S saída | B balanço (a quantidade é a contagem: o físico passa a ser ela). */
export const STOCK_MOVEMENT_KINDS = ["E", "S", "B"] as const;
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];

/** Header das escritas com efeito (04-api §1). Obrigatório nos POST de estoque. */
export const IDEMPOTENCY_HEADER = "Idempotency-Key";
export const idempotencyKey = z
  .string()
  .regex(/^[A-Za-z0-9_.:-]{8,100}$/, "Idempotency-Key inválida (8 a 100 letras, números, _ . : -)");

const qty = (min: number) =>
  z
    .number("Informe a quantidade")
    .int("Quantidade inválida")
    .min(min, min > 0 ? "Quantidade deve ser maior que zero" : "Quantidade não pode ser negativa")
    .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite");
const notes = text(500);

/** Maior custo unitário de uma entrada: R$ 1 bilhão (10^11 centavos). */
export const MAX_UNIT_COST_CENTS = 100_000_000_000;

export const warehouseInput = z.object({
  name: z.string().trim().min(1, "Informe o nome").max(60, "Até 60 caracteres"),
  isDefault: z.boolean().default(false),
  countsInTotal: z.boolean().default(true),
  active: z.boolean().default(true),
});
export type WarehouseInput = z.infer<typeof warehouseInput>;
export type WarehouseView = WarehouseInput & { id: string };

export const stockSettingsInput = z.object({ allowNegative: z.boolean() });
export type StockSettings = z.infer<typeof stockSettingsInput>;

/** Lançamento manual (RF-SUP-01). Custo unitário só na entrada e no balanço (vale para o que o balanço acrescenta). */
export const stockMovementInput = z
  .object({
    productId: ulidParam,
    warehouseId: ulidParam,
    kind: z.enum(STOCK_MOVEMENT_KINDS, "Tipo inválido"),
    qtyQ4: qty(0),
    unitCostCents: z
      .number()
      .int("Use centavos inteiros")
      .min(0, "Custo não pode ser negativo")
      .max(MAX_UNIT_COST_CENTS, "Custo acima do limite")
      .nullish()
      .transform((v) => v ?? null),
    notes,
    /** Entrada: lote (obrigatório no produto com controle de lotes). */
    lot: lotInput.nullish().transform((v) => v ?? null),
    /** Saída: escolha manual dos lotes; ausente = FEFO. */
    lots: lotTakes,
  })
  .superRefine((v, ctx) => {
    if (v.lot && v.kind !== "E")
      ctx.addIssue({ code: "custom", path: ["lot"], message: "Lote informado só na entrada" });
    if (v.lots && v.kind !== "S")
      ctx.addIssue({ code: "custom", path: ["lots"], message: "Escolha de lotes só na saída" });
    if (v.kind !== "B" && v.qtyQ4 === 0)
      ctx.addIssue({ code: "custom", path: ["qtyQ4"], message: "Quantidade deve ser maior que zero" });
    if (v.kind === "S" && v.unitCostCents !== null)
      ctx.addIssue({
        code: "custom",
        path: ["unitCostCents"],
        message: "A saída usa o custo médio; informe custo só na entrada ou no balanço",
      });
  });
export type StockMovementInput = z.infer<typeof stockMovementInput>;

export const stockTransferInput = z
  .object({
    productId: ulidParam,
    fromWarehouseId: ulidParam,
    toWarehouseId: ulidParam,
    qtyQ4: qty(1),
    notes,
    /** Escolha manual dos lotes que saem da origem; ausente = FEFO. Entram no destino os mesmos lotes. */
    lots: lotTakes,
  })
  .refine((v) => v.fromWarehouseId !== v.toWarehouseId, {
    path: ["toWarehouseId"],
    message: "Escolha um depósito de destino diferente do de origem",
  });
export type StockTransferInput = z.infer<typeof stockTransferInput>;

export const stockReverseInput = z.object({ notes });
export type StockReverseInput = z.infer<typeof stockReverseInput>;

export const stockProductQuery = z.object({ productId: ulidParam });
/** `withTotal=true` conta o total do filtro em `summary.count` (só na 1ª página); sem ele, -1. */
export const stockMovementQuery = listQuery.omit({ q: true }).extend({
  productId: ulidParam,
  warehouseId: ulidParam.optional(),
  withTotal: z.stringbool({ error: "Use true ou false" }).optional(),
});
export type StockMovementQuery = z.infer<typeof stockMovementQuery>;

/** Reserva feita por outro módulo (pedido de venda, T1.6/T1.8). Só pelo RPC do DO; não há rota de escrita. */
export type StockReserveInput = {
  productId: string;
  warehouseId: string;
  qtyQ4: number;
  originType: string;
  originId: string;
};

/**
 * Saldo do produto num depósito. Kit (E): físico e reservado nulos e o disponível derivado (null = sem limite).
 * `valueCents` (físico × custo médio) só com products:read_cost; null quando passa de 2^53 (a tela mostra "—").
 */
export type StockBalanceRow = {
  warehouseId: string;
  warehouseName: string;
  isDefault: boolean;
  countsInTotal: boolean;
  active: boolean;
  physicalQ4: number | null;
  reservedQ4: number | null;
  availableQ4: number | null;
  valueCents?: number | null;
};

export type StockBalanceView = {
  productId: string;
  format: "S" | "V" | "E";
  unit: string;
  stockControl: boolean;
  /** `kit`: disponível derivado dos componentes; `variations`: soma das variações; null: saldo próprio. */
  derived: "kit" | "variations" | null;
  rows: StockBalanceRow[];
  /** Soma dos depósitos com "considera no saldo total". */
  total: Omit<StockBalanceRow, "warehouseId" | "warehouseName" | "isDefault" | "countsInTotal" | "active">;
  /** Só com products:read_cost e saldo próprio. */
  avgCostCents?: number;
};

export type StockMovementView = {
  id: string;
  productId: string;
  productName: string;
  warehouseId: string;
  warehouseName: string;
  kind: StockMovementKind;
  qtyQ4: number;
  deltaQ4: number;
  balanceAfterQ4: number;
  unitCostCents?: number | null;
  originType: string | null;
  originId: string | null;
  transferGroupId: string | null;
  reversesId: string | null;
  reversedById: string | null;
  /** Lotes do movimento (T3.2); vazio = "Sem lote" ou produto sem controle de lotes. */
  lots: MovementLotView[];
  notes: string | null;
  occurredAt: string;
  createdBy: string | null;
};

export type StockReservationView = {
  id: string;
  warehouseId: string;
  warehouseName: string;
  qtyQ4: number;
  originType: string;
  originId: string;
  createdAt: string;
};

export type StockPositionView = { physicalQ4: number; reservedQ4: number; availableQ4: number };
export type StockMovementResult = { id: string; balance: StockPositionView };
export type StockTransferResult = { transferGroupId: string; movementIds: string[] };
export type StockReverseResult = { movementIds: string[] };
